import { supabase } from "@/integrations/supabase/client";
import { Service } from "@/types/dashboard";
import { ProfileData } from "@/hooks/useProfileSettings";

/**
 * Ensures the user's email is properly synchronized between auth and profiles tables
 * @param userId The user's UUID
 * @param userEmail The user's email address
 * @returns Object containing success status and any error message
 */
export const syncUserEmail = async (userId: string, userEmail: string): Promise<{success: boolean; message?: string}> => {
  if (!userId || !userEmail) {
    console.error("syncUserEmail called with missing userId or userEmail");
    return { success: false, message: "Missing required parameters" };
  }

  try {
    console.log(`Syncing email for user ${userId}`);
    
    // First check if profile exists
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', userId)
      .single();
    
    if (fetchError) {
      console.warn(`Profile not found for user ${userId}, creating new profile`);
      
      // Profile doesn't exist, create it
      const { error: insertError } = await supabase
        .from('profiles')
        .insert([{ id: userId, email: userEmail }]);
      
      if (insertError) {
        console.error("Failed to create profile:", insertError.message);
        return { success: false, message: "Failed to create profile" };
      }
      
      console.log("Created new profile with email");
      return { success: true, message: "Profile created with email" };
    }
    
    // Profile exists, check if email needs updating
    if (profile.email !== userEmail) {
      console.log("Email mismatch, updating profile email");
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ email: userEmail })
        .eq('id', userId);
      
      if (updateError) {
        console.error("Failed to update email:", updateError.message);
        return { success: false, message: "Failed to update email" };
      }
      
      console.log("Email updated successfully");
      return { success: true, message: "Email updated" };
    }
    
    // Email already matches
    console.log("Email already synchronized, no update needed");
    return { success: true, message: "Email already synchronized" };
  } catch (error) {
    console.error("Error in syncUserEmail:", error);
    return { success: false, message: "Unexpected error during email synchronization" };
  }
};

// Profile completeness scoring utility
export interface ProfileCompletenessResult {
  overallScore: number;
  sectionScores: Record<string, number>;
  missingItems: string[];
  recommendations: string[];
  missingFieldsBySection: Record<string, string[]>;
}

export function calculateProfileCompleteness(profile: ProfileData, services: Service[]): ProfileCompletenessResult {
  // Section weights
  const weights = {
    personalInfo: 15,
    businessInfo: 20,
    businessImages: 15,
    socialLinks: 10,
    services: 25,
    availability: 10,
    notifications: 5,
  };

  // Track missing fields by section
  const missingFieldsBySection: Record<string, string[]> = {
    personalInfo: [],
    businessInfo: [],
    businessImages: [],
    socialLinks: [],
    services: [],
    availability: [],
    notifications: [],
  };

  // 1. Personal Info
  let personalInfoScore = 0;
  if (profile.first_name) personalInfoScore += 3; else missingFieldsBySection.personalInfo.push("First Name");
  if (profile.last_name) personalInfoScore += 3; else missingFieldsBySection.personalInfo.push("Last Name");
  if (profile.country) personalInfoScore += 2; else missingFieldsBySection.personalInfo.push("Country");
  if (profile.currency) personalInfoScore += 2; else missingFieldsBySection.personalInfo.push("Currency");
  if (profile.whatsapp_number) personalInfoScore += 3; else missingFieldsBySection.personalInfo.push("Phone Number");
  if (profile.preferred_messaging) personalInfoScore += 2; else missingFieldsBySection.personalInfo.push("Preferred Messaging");
  personalInfoScore = Math.min(personalInfoScore, weights.personalInfo);

  // 2. Business Info
  let businessInfoScore = 0;
  if (profile.company_name) businessInfoScore += 8; else missingFieldsBySection.businessInfo.push("Business Name");
  if (profile.profession) businessInfoScore += 6; else missingFieldsBySection.businessInfo.push("Profession");
  if (profile.profile_bio) businessInfoScore += (profile.profile_bio.length > 100 ? 6 : 3); else missingFieldsBySection.businessInfo.push("Profile Bio");
  businessInfoScore = Math.min(businessInfoScore, weights.businessInfo);

  // 3. Business Images
  let businessImagesScore = 0;
  if (profile.profile_image_url) businessImagesScore += 10; else missingFieldsBySection.businessImages.push("Profile Image");
  if (profile.banner_image_url) businessImagesScore += 5; else missingFieldsBySection.businessImages.push("Banner Image");
  businessImagesScore = Math.min(businessImagesScore, weights.businessImages);

  // 4. Social Links (now: at least 1 enabled = complete)
  let socialLinksScore = 0;
  // Assume profile.social_links is an array of enabled links, or use a similar check if available
  // For demo, check if enabledSocialLinks > 0
  const enabledSocialLinks = 2; // <-- Replace with real count if available
  if (enabledSocialLinks > 0) {
    socialLinksScore = weights.socialLinks;
  } else {
    missingFieldsBySection.socialLinks.push("Add at least one social link");
  }
  socialLinksScore = Math.min(socialLinksScore, weights.socialLinks);

  // 5. Services (quality over quantity, now: at least 1 fully complete = complete)
  let servicesScore = 0;
  let hasFullyCompleteService = false;
  if (services.length > 0) {
    // Check for at least one fully complete service
    for (const service of services) {
      const isComplete =
        service.name && service.name.length > 3 &&
        service.description && service.description.length > 50 &&
        service.price && service.price > 0 &&
        service.image_url &&
        service.service_mode && service.service_area &&
        service.duration && service.requires_scheduling !== undefined;
      if (isComplete) {
        hasFullyCompleteService = true;
        break;
      }
    }
    if (hasFullyCompleteService) {
      servicesScore = weights.services;
    } else {
      missingFieldsBySection.services.push("Add at least one fully complete service");
    }
  } else {
    missingFieldsBySection.services.push("Add at least one service");
  }
  servicesScore = Math.min(servicesScore, weights.services);

  // 6. Availability (placeholder, real logic should check actual settings)
  let availabilityScore = 0;
  // Assume available for demo
  availabilityScore = weights.availability;
  // If not available, push missing field (for real logic)
  // missingFieldsBySection.availability.push("Set your availability");

  // 7. Notifications (placeholder, real logic should check actual settings)
  let notificationsScore = 0;
  // Assume enabled for demo
  notificationsScore = weights.notifications;
  // If not enabled, push missing field (for real logic)
  // missingFieldsBySection.notifications.push("Enable notifications");

  // Section scores
  const sectionScores = {
    personalInfo: personalInfoScore,
    businessInfo: businessInfoScore,
    businessImages: businessImagesScore,
    socialLinks: socialLinksScore,
    services: Math.round(servicesScore),
    availability: availabilityScore,
    notifications: notificationsScore,
  };

  // Overall score
  const overallScore = Object.values(sectionScores).reduce((a, b) => a + b, 0);

  // Missing items (flat list for backward compatibility)
  const missingItems = [
    ...missingFieldsBySection.personalInfo,
    ...missingFieldsBySection.businessInfo,
    ...missingFieldsBySection.businessImages,
    ...missingFieldsBySection.socialLinks,
    ...missingFieldsBySection.services,
    ...missingFieldsBySection.availability,
    ...missingFieldsBySection.notifications,
  ];

  // Recommendations (simplified for demo)
  const recommendations = [];
  if (sectionScores.services < 15 && services.length > 0) recommendations.push("Customize your default services to make them more appealing");
  if (sectionScores.businessInfo < 10) recommendations.push("Complete your business information");

  return {
    overallScore,
    sectionScores,
    missingItems,
    recommendations,
    missingFieldsBySection,
  };
}

// Export other profile-related utilities here 